import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import {
  createAdminUser,
  listAdminUserGroups,
  listAdminUsers,
  resetAdminUserTemporaryPassword,
  setAdminUserEnabled,
  updateAdminUserGroup,
} from '../../utils/adminUsersApi';
import { mapAdminApiError } from '../../utils/adminApiErrors';

const { Paragraph, Text } = Typography;

const AdminUsersPanel = ({ principal }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [paginationToken, setPaginationToken] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [form] = Form.useForm();

  const isAdmin = Boolean(principal?.isAdmin);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const payload = await listAdminUsers({ limit: 30 });
      setUsers(payload.users || []);
      setPaginationToken(payload.pagination_token || null);
      setAdminError(null);
    } catch (error) {
      const mapped = mapAdminApiError(error);
      setAdminError(mapped);
      message.error(mapped.title);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      void loadUsers();
    }
  }, [isAdmin]);

  const handleCreateUser = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = await createAdminUser({
        email: values.email,
        temporaryPassword: values.temporaryPassword,
      });
      setCreateOpen(false);
      form.resetFields();
      message.success(`Usuario criado: ${payload?.data?.user?.username || values.email}`);
      void loadUsers();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.response?.data?.message || error?.message || 'Falha ao criar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleResetTempPassword = async (username) => {
    const temporaryPassword = window.prompt(`Nova senha temporaria para ${username}`)?.trim();
    if (!temporaryPassword) {
      return;
    }
    setLoading(true);
    try {
      await resetAdminUserTemporaryPassword({ username, temporaryPassword });
      message.success('Senha temporaria aplicada.');
      void loadUsers();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || 'Falha ao resetar senha temporaria');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (username, enabled) => {
    setLoading(true);
    try {
      await setAdminUserEnabled({ username, enabled });
      message.success(enabled ? 'Usuario habilitado' : 'Usuario desabilitado');
      void loadUsers();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || 'Falha ao atualizar status do usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroup = async (username) => {
    const groupName = window.prompt(`Adicionar grupo para ${username}`)?.trim();
    if (!groupName) {
      return;
    }
    setLoading(true);
    try {
      await updateAdminUserGroup({ username, groupName, operation: 'add' });
      message.success('Grupo adicionado');
      void loadUsers();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || 'Falha ao adicionar grupo');
    } finally {
      setLoading(false);
    }
  };

  const handleListGroups = async (username) => {
    setLoading(true);
    try {
      const payload = await listAdminUserGroups({ username });
      const groups = payload?.data?.groups || [];
      message.info(groups.length ? `Grupos: ${groups.join(', ')}` : 'Usuario sem grupos');
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || 'Falha ao listar grupos');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Username',
        dataIndex: 'username',
        key: 'username',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: (value) => value || '-',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (value) => <Tag>{value || 'UNKNOWN'}</Tag>,
      },
      {
        title: 'Enabled',
        dataIndex: 'enabled',
        key: 'enabled',
        render: (value) => (value ? <Tag color="green">true</Tag> : <Tag color="red">false</Tag>),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space wrap>
            <Button size="small" onClick={() => handleListGroups(record.username)}>
              Grupos
            </Button>
            <Button size="small" onClick={() => handleAddGroup(record.username)}>
              Add grupo
            </Button>
            <Button size="small" onClick={() => handleResetTempPassword(record.username)}>
              Temp senha
            </Button>
            <Popconfirm
              title={record.enabled ? 'Desabilitar usuario?' : 'Habilitar usuario?'}
              onConfirm={() => handleToggleEnabled(record.username, !record.enabled)}
            >
              <Button size="small" danger={record.enabled}>
                {record.enabled ? 'Desabilitar' : 'Habilitar'}
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    []
  );

  if (!isAdmin) {
    return <Alert type="error" message="Acesso negado" description="Somente administradores podem acessar este painel." />;
  }

  return (
    <div>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {adminError && (
          <Alert
            type="error"
            showIcon
            message={adminError.title}
            description={adminError.description}
          />
        )}
        <div>
          <Typography.Title level={3}>Painel Admin - Usuarios Cognito</Typography.Title>
          <Paragraph>
            Operacoes disponiveis: criar usuario, reset temporario, habilitar/desabilitar e gestao de grupos.
          </Paragraph>
          <Text type="secondary">Próxima página disponível: {paginationToken ? 'sim' : 'nao'}</Text>
        </div>

        <Space>
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            Novo usuario
          </Button>
          <Button onClick={() => void loadUsers()} loading={loading}>
            Atualizar
          </Button>
        </Space>

        <Table
          rowKey={(row) => row.username}
          loading={loading}
          columns={columns}
          dataSource={users}
          pagination={false}
        />
      </Space>

      <Modal
        title="Criar usuario Cognito"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreateUser}
        okText="Criar"
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Informe o email' },
              { type: 'email', message: 'Email invalido' },
            ]}
          >
            <Input placeholder="usuario@dominio.com" />
          </Form.Item>
          <Form.Item
            name="temporaryPassword"
            label="Senha temporaria (opcional)"
            rules={[{ min: 8, message: 'Minimo 8 caracteres' }]}
          >
            <Input.Password placeholder="Se vazio, backend gera aleatoria" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsersPanel;
